import React, { useState } from "react";  
import { useNavigate } from "react-router-dom";  
import api from "../api";  
import Navbar from "./Navbar";  
import { motion, AnimatePresence } from "framer-motion";  
  
const AddAPI = () => {  
  const navigate = useNavigate();  
  const [apiKey, setApiKey] = useState("");  
  const [apiSecret, setApiSecret] = useState("");  
  const [testnet, setTestnet] = useState(false);  
  const [message, setMessage] = useState("");  
  const [messageType, setMessageType] = useState("");  
  const [balance, setBalance] = useState(null);  
  const [isLoading, setIsLoading] = useState(false);  
  const [showSecret, setShowSecret] = useState(false);  
  const [keysAdded, setKeysAdded] = useState(false);  
  
  const handleSubmit = async (e) => {  
    e.preventDefault();  
    setIsLoading(true);  
    setMessage("");  
    setBalance(null);  
    setKeysAdded(false);  
  
    try {  
      const res = await api.post("users/add_api_keys", {  
        api_key: apiKey,  
        api_secret: apiSecret,  
        testnet,  
      });  
      setMessage(res.data.msg);  
      setMessageType("success");  
      setBalance(res.data.balance);  
      setKeysAdded(true);  
      setApiKey("");  
      setApiSecret("");  
    } catch (error) {  
      console.error("Failed to add API keys:", error);  
      setBalance(null);  
      setMessageType("error");  
      setKeysAdded(false);  
      if (error.response?.data?.detail) setMessage(error.response.data.detail);  
      else if (error.message === "Network Error")  
        setMessage("Can't reach the server. Check your connection.");  
      else setMessage("Failed to save keys. Please verify your credentials.");  
    } finally {  
      setIsLoading(false);  
    }  
  };  
  
  return (  
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white">  
      <Navbar />  
  
      <div className="max-w-2xl mx-auto py-6 sm:py-10 px-4 sm:px-6 lg:px-8">  
        {/* Header */}  
        <motion.div  
          initial={{ opacity: 0, y: 12 }}  
          animate={{ opacity: 1, y: 0 }}  
          className="mb-6 sm:mb-8"  
        >  
          <button  
            onClick={() => navigate("/accounts")}  
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gold mb-4 transition-colors"  
          >  
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">  
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />  
            </svg>  
            Back to accounts  
          </button>  
  
          <div className="flex items-center gap-3">  
            <div className="w-1 h-10 bg-gradient-to-b from-gold to-yellow-600 rounded-full" />  
            <div>  
              <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">  
                Connect Binance  
              </h1>  
              <p className="text-gray-400 text-sm mt-1">  
                Add your API keys to enable Bitcoin & crypto trading  
              </p>  
            </div>  
          </div>  
        </motion.div>  
  
        {/* Form Card */}  
        <motion.div  
          initial={{ opacity: 0, y: 12 }}  
          animate={{ opacity: 1, y: 0 }}  
          transition={{ delay: 0.05 }}  
          className="bg-gradient-to-br from-gray-900/60 via-black/60 to-gray-900/60 backdrop-blur-xl rounded-2xl border border-gray-800 p-6 sm:p-7 shadow-2xl"  
        >  
          <form onSubmit={handleSubmit} className="space-y-5">  
            {/* Environment selector */}  
            <div>  
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">  
                Environment  
              </label>  
              <div className="grid grid-cols-2 gap-2 p-1 bg-black/40 border border-gray-800 rounded-xl">  
                <button  
                  type="button"  
                  onClick={() => setTestnet(true)}  
                  disabled={isLoading}  
                  className={`py-2.5 rounded-lg text-sm font-semibold transition-all ${  
                    testnet  
                      ? "bg-gold/10 text-gold border border-gold/40"  
                      : "text-gray-400 hover:text-white border border-transparent"  
                  }`}  
                >  
                  Testnet  
                </button>  
                <button  
                  type="button"  
                  onClick={() => setTestnet(false)}  
                  disabled={isLoading}  
                  className={`py-2.5 rounded-lg text-sm font-semibold transition-all ${  
                    !testnet  
                      ? "bg-red-500/10 text-red-400 border border-red-500/40"  
                      : "text-gray-400 hover:text-white border border-transparent"  
                  }`}  
                >  
                  Live  
                </button>  
              </div>  
              <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">  
                {testnet  
                  ? "Use virtual funds on Binance's test environment — no real money at risk."  
                  : "Live trading uses real money."}  
              </p>  
            </div>  
  
            {/* API Key */}  
            <FormField  
              label="API Key"  
              value={apiKey}  
              onChange={setApiKey}  
              placeholder="Paste your Binance API Key"  
              disabled={isLoading}  
              required  
            />  
  
            {/* API Secret */}  
            <FormField  
              label="API Secret"  
              value={apiSecret}  
              onChange={setApiSecret}  
              placeholder="Paste your Binance API Secret"  
              type={showSecret ? "text" : "password"}  
              disabled={isLoading}  
              required  
              trailing={  
                <button  
                  type="button"  
                  onClick={() => setShowSecret(!showSecret)}  
                  className="text-gray-500 hover:text-gold transition-colors"  
                  tabIndex={-1}  
                >  
                  {showSecret ? (  
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
                      <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />  
                      <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />  
                    </svg>  
                  ) : (  
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />  
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />  
                    </svg>  
                  )}  
                </button>  
              }  
            />  
  
            {/* Submit */}  
            <motion.button  
              type="submit"  
              disabled={isLoading}  
              whileHover={{ scale: isLoading ? 1 : 1.01 }}  
              whileTap={{ scale: isLoading ? 1 : 0.99 }}  
              className={`w-full py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${  
                isLoading  
                  ? "bg-gray-800 text-gray-500 cursor-not-allowed"  
                  : "bg-gradient-to-r from-gold to-yellow-600 text-black hover:from-yellow-500 hover:to-gold shadow-lg shadow-gold/20"  
              }`}  
            >  
              {isLoading ? (  
                <>  
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />  
                  Validating…  
                </>  
              ) : (  
                <>  
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />  
                  </svg>  
                  Validate & save keys  
                </>  
              )}  
            </motion.button>  
          </form>  
  
          {/* Result message */}  
          <AnimatePresence>  
            {message && (  
              <motion.div  
                initial={{ opacity: 0, y: -6 }}  
                animate={{ opacity: 1, y: 0 }}  
                exit={{ opacity: 0, y: -6 }}  
                className={`mt-5 p-4 rounded-xl border ${  
                  messageType === "success"  
                    ? "bg-green-900/20 border-green-500/40"  
                    : "bg-red-900/20 border-red-500/40"  
                }`}  
              >  
                <div className="flex items-start gap-2.5">  
                  {messageType === "success" ? (  
                    <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">  
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />  
                    </svg>  
                  ) : (  
                    <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">  
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />  
                    </svg>  
                  )}  
                  <p className={`text-sm font-medium ${messageType === "success" ? "text-green-300" : "text-red-300"}`}>  
                    {message}  
                  </p>  
                </div>  
              </motion.div>  
            )}  
          </AnimatePresence>  
  
          {/* Balance */}  
          <AnimatePresence>  
            {balance && (  
              <motion.div  
                initial={{ opacity: 0, y: 12 }}  
                animate={{ opacity: 1, y: 0 }}  
                exit={{ opacity: 0, y: -6 }}  
                className="mt-5"  
              >  
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">  
                  Account balance  
                </p>  
                <div className="grid grid-cols-2 gap-2">  
                  {Object.entries(balance).map(([asset, amount]) => (  
                    <div  
                      key={asset}  
                      className="flex items-center justify-between px-3 py-2.5 bg-black/40 border border-gray-800 rounded-lg"  
                    >  
                      <span className="text-xs text-gray-500 font-medium">{asset}</span>  
                      <span className="text-sm font-bold text-white">  
                        {Number(amount).toFixed(4)}  
                      </span>  
                    </div>  
                  ))}  
                </div>  
              </motion.div>  
            )}  
          </AnimatePresence>  
  
          {/* Success CTA */}  
          <AnimatePresence>  
            {keysAdded && (  
              <motion.button  
                initial={{ opacity: 0, y: 12 }}  
                animate={{ opacity: 1, y: 0 }}  
                exit={{ opacity: 0 }}  
                onClick={() => navigate("/my-models")}  
                className="mt-5 w-full py-3 rounded-xl font-bold text-sm bg-green-500/10 border border-green-500/40 text-green-400 hover:bg-green-500/20 transition-all flex items-center justify-center gap-2"  
              >  
                Continue to My Models  
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />  
                </svg>  
              </motion.button>  
            )}  
          </AnimatePresence>  
        </motion.div>  
  
        {/* Security + Help footer */}  
        <motion.div  
          initial={{ opacity: 0 }}  
          animate={{ opacity: 1 }}  
          transition={{ delay: 0.2 }}  
          className="mt-5 space-y-3"  
        >  
          <div className="bg-black/30 border border-gray-800 rounded-xl p-4 backdrop-blur-sm">  
            <div className="flex items-start gap-3">  
              <div className="w-7 h-7 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-center flex-shrink-0">  
                <svg className="w-3.5 h-3.5 text-green-400" fill="currentColor" viewBox="0 0 20 20">  
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />  
                </svg>  
              </div>  
              <p className="text-xs text-gray-400 leading-relaxed">  
                Keys are encrypted with AES-256 before being stored. Make sure your Binance API key has <span className="text-white font-semibold">trading permission only</span> — never enable withdrawals.  
              </p>  
            </div>  
          </div>  
  
          <div className="text-center">  
            <a  
              href="https://www.binance.com/en/support/faq/how-to-create-api-360002502072"  
              target="_blank"  
              rel="noopener noreferrer"  
              className="text-xs text-gray-500 hover:text-gold inline-flex items-center gap-1.5 transition-colors"  
            >  
              How to create Binance API keys  
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">  
                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />  
                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />  
              </svg>  
            </a>  
          </div>  
        </motion.div>  
      </div>  
    </div>  
  );  
};  
  
/* ─── Reusable Form Field ─── */  
const FormField = ({  
  label,  
  value,  
  onChange,  
  placeholder,  
  type = "text",  
  disabled,  
  required,  
  trailing,  
  helper,  
}) => (  
  <div>  
    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">  
      {label}  
    </label>  
    <div className="relative">  
      <input  
        type={type}  
        value={value}  
        onChange={(e) => onChange(e.target.value)}  
        placeholder={placeholder}  
        disabled={disabled}  
        required={required}  
        className={`w-full px-4 py-3 bg-black/40 border border-gray-800 focus:border-gold/50 focus:ring-1 focus:ring-gold/30 rounded-xl text-white text-sm placeholder-gray-600 outline-none transition-all ${  
          trailing ? "pr-11" : ""  
        }`}  
      />  
      {trailing && (  
        <div className="absolute right-3 top-1/2 -translate-y-1/2">{trailing}</div>  
      )}  
    </div>  
    {helper && <p className="text-[11px] text-gray-500 mt-1.5">{helper}</p>}  
  </div>  
);  
  
export default AddAPI;  